import { UseInstructor, UseTag } from '@core/usecases';
import { InstructorEntity, InstructorsListEntity, Pagination, UserEntity } from '@core/entities';
import { FormService, Logger } from '@core/services';
import { FilterQuery, ScheduleColumn, ScheduleItem } from '@core/types';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DropDownItem } from '@core/types/Dropdown.type';
import { DateTimeUtility, EnumToKeyValueArray, GetTeamId, GetUserId, isMobileView, removeNullAndUndefinedProperties, scrollToTop, timeRangeValidator } from '@core/utils';
import { ConfirmationDialogType, DeleteConfirmationOptions, InstructorType, NotificationType, SocketEvents, TagType } from '@core/enums';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { instanceToPlain } from 'class-transformer';
import { PermissionService } from '@auth';
import { ActivatedRoute, Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';
import { forkJoin } from 'rxjs';
import { convertInstructorsToScheduleData } from '@shared/compositions/helpers';

const log = new Logger('Instructors Composition');

export class InstructorsComposition extends FormService {
  instructors: InstructorEntity[] = [];
  pagination: Pagination = {
    page: 1,
    pageSize: 20,
  };
  filterQuery: FilterQuery = {
    tags: null,
    instructorType: null,
  };
  instructorTags: DropDownItem[] = [];

  // For Instructor Form
  currentInstructor: InstructorEntity;
  instructorTypes: DropDownItem[] = [];
  days: DropDownItem[] = [];
  selectedImage: File | string;
  internalInstructorsUsers: UserEntity[] = [];

  // ---- Specifically for Schedule View in instructors
  isScheduleView = false;
  instructorScheduleColumns: ScheduleColumn[];
  instructorScheduleItems: ScheduleItem<InstructorEntity>[] = [];
  isLoadingForSchedule = true;

  protected readonly _translate = inject(TranslateService);
  protected _dialogRef: DialogRef<any, boolean>;

  protected readonly _bottomSheet: NgxBottomSheetService;
  protected readonly _dialogService: DialogService;
  protected readonly _socketService = inject(SocketIoService);
  protected readonly _activatedRoute = inject(ActivatedRoute);

  private readonly _useInstructor = new UseInstructor();
  private readonly _useTags = new UseTag();

  private readonly _formBuilder = inject(FormBuilder);
  private readonly _permissionService = inject(PermissionService);
  private readonly _router = inject(Router);
  private readonly _toast = inject(HotToastService);

  constructor() {
    super();
    this.instructorTypes = EnumToKeyValueArray(InstructorType, this._translate);
    this.days = DateTimeUtility.getWeekDays(this._translate) as DropDownItem[];

    if (isMobileView()) {
      this._bottomSheet = inject(NgxBottomSheetService);
    } else {
      this._dialogService = inject(DialogService);
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ----------------------------------------------------- GETTERS -------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  get isUserAdmin() {
    return this._permissionService.isAdmin();
  }

  get isUserAuthor() {
    return +this.currentInstructor?.author === +GetUserId();
  }

  get allDaysSelected() {
    return this.weekdaysFormArray.length === this.days.length;
  }

  get qualificationsFormArray() {
    return this.form.get('qualifications') as FormArray;
  }

  get weekdaysFormArray() {
    return this.form.get('weekdays') as FormArray;
  }

  get hasConnectedBookings() {
    return !!(this.currentInstructor?.instructorBookings?.length || this.currentInstructor?.instructorBookingDetails?.length);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------------ ACTIONS ------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  onPaginationChange(pagination: Pagination) {
    this.pagination = pagination;

    if (this.isScheduleView) {
      this.fetchInstructorsForScheduleView();
    } else {
      this.fetchAllInstructors();
    }
    scrollToTop(!isMobileView() ? '.instructors' : null);
  }

  addWeekdaysRow(data?: { day: number; time_from: string; time_to: string }) {
    if (this.hasConnectedBookings && !data) {
      return;
    }

    const weekdays = this.weekdaysFormArray;
    const newGroup = this._formBuilder.group(
      {
        day: [data?.day, [Validators.required]],
        time_from: [data?.time_from || null, [Validators.required]],
        time_to: [data?.time_to || null, [Validators.required]],
      },
      { validators: timeRangeValidator('time_from', 'time_to') },
    );

    weekdays.push(newGroup);

    weekdays.controls.forEach((group) => {
      const dayControl = group.get('day');
      if (dayControl) {
        dayControl.setValidators([Validators.required]);
        dayControl.updateValueAndValidity({ emitEvent: false });
        dayControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  removeWeekdaysRow(index: number) {
    if (this.hasConnectedBookings) {
      return;
    }

    const weekdays = this.form.get('weekdays') as FormArray;
    weekdays.removeAt(index);
  }

  addQualificationsRow() {
    const qualifications = this.form.get('qualifications') as FormArray;
    qualifications.push(this._formBuilder.control(null));
  }

  removeQualificationsRow(index: number) {
    this.qualificationsFormArray.removeAt(index);
  }

  deleteInstructor(instructor: InstructorEntity, btnRef?: ButtonComponent) {
    if (!instructor.id) return;

    btnRef?.showLoading();

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheet.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: instructor.firstName + ' ' + instructor.lastName,
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useInstructor.deleteInstructor(instructor.id), btnRef)
                  .then(() => {
                    this._toast.success(this._translate.instant('Delete Successful'));
                    this._socketService.emit(SocketEvents.SendNotification, NotificationType.Instructor);
                    if (this._dialogService) this._dialogService.closeAll();
                    this._reloadCurrentRoute();
                  })
                  .catch((err) => {
                    this._toast.error(this.error || this._translate.instant('Something went wrong'));
                  });
              }
              break;
            default:
              btnRef?.showIdle();
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: instructor.firstName + ' ' + instructor.lastName,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this.call(this._useInstructor.deleteInstructor(instructor.id), btnRef)
              .then(() => {
                this._toast.success(this._translate.instant('Delete Successful'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Instructor);
                if (this._bottomSheet) this._bottomSheet.closeAll();
                this._reloadCurrentRoute();
              })
              .catch((err) => {
                this._toast.error(this.error || this._translate.instant('Something went wrong'));
              });
          }
          btnRef?.showIdle();
        },
      });
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // -------------------------------------------- FETCH DATA Functions ---------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  fetchAllInstructors() {
    this.call(this._useInstructor.getAllInstructors(this.pagination, this.filterQuery))
      .then((res: InstructorsListEntity) => {
        if (res) {
          this.instructors = res.instructors;
          this.pagination = res.pagination;
        } else {
          log.info('No Instructor found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching Instructors', err);
      });
  }

  fetchInstructorsForScheduleView() {
    this.isLoadingForSchedule = true;
    this._useInstructor
      .getAllInstructors(this.pagination, this.filterQuery)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: InstructorsListEntity) => {
          if (res) {
            this.instructors = res.instructors;
            this.pagination = res.pagination;
            this.fetchInstructorDetails();
          } else {
            log.info('No Instructor found');
          }
        },
        error: (err) => {
          log.error('Error while fetching Instructors for Schedule View', err);
        },
      });
  }

  fetchInstructorDetails() {
    const detailRequests = this.instructors.map((instructor) => this._useInstructor.getInstructorById(instructor.id));

    forkJoin(detailRequests)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (detailedInstructors: InstructorEntity[]) => {
          this.instructors = detailedInstructors;
          const { columns, items } = convertInstructorsToScheduleData(this.instructors);
          this.instructorScheduleItems = items;
          this.instructorScheduleColumns = columns;
          this.isLoadingForSchedule = false;
        },
        error: (err) => {
          log.error('Error while fetching Instructor details', err);
        },
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _InitDataForForm() {
    this._fetchTags();
    this._fetchInternalInstructors();
    this._initForm();
    this.addWeekdaysRow();
    this.addQualificationsRow();
  }

  protected _createInstructor(data: InstructorEntity, btnRef?: ButtonComponent) {
    data.author = GetUserId();
    data.approvedStatus = this._permissionService.isAdmin() || this._permissionService.isFunctionary() ? 1 : 0;
    const body = removeNullAndUndefinedProperties(instanceToPlain(new InstructorEntity(data)));
    body.instructor_type = data.type;
    body.tags = data.tags;
    body.team_id = GetTeamId();
    if (data.type === InstructorType.External) {
      delete body.internal_id;
      body.internal = false;
    } else {
      body.internal = true;
    }
    delete body.courses_type;

    const formData = this.jsonToFromData(body, ['tags', 'weekdays', 'qualifications']);

    if (this.currentInstructor?.addImg) {
      formData.append('add_img', this.currentInstructor?.addImg);
    }

    if (this.selectedImage) {
      formData.append('file', this.selectedImage);
    }

    const service$ = this.currentInstructor ? this._useInstructor.updateInstructor(this.currentInstructor.id, formData) : this._useInstructor.createInstructor(formData);

    this.call(service$, btnRef)
      .then((response) => {
        if (response) {
          if (!this._permissionService.isAdmin()) {
            this._toast.success(this._translate.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
          } else {
            const message = this.currentInstructor && this.currentInstructor?.id ? 'Update Successful' : 'Created Successfully';
            this._toast.success(this._translate.instant(message));
          }

          this._socketService.emit(SocketEvents.SendNotification, NotificationType.Instructor);
          if (!isMobileView()) {
            this._dialogRef.close(true);
          } else {
            this._bottomSheet.closeAll();
          }
          this._reloadCurrentRoute();
        } else {
          this._toast.error(this._translate.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while creating or updating instructor');
      });
  }

  protected _patchFormValues(instructor: InstructorEntity) {
    const tags = instructor?.instructorTags ? instructor.instructorTags.map((tag) => tag.tagName) : [];

    this.form.patchValue({
      firstName: instructor.firstName,
      lastName: instructor.lastName,
      email: instructor.email,
      phoneNo: instructor.phoneNo,
      address: instructor.address,
      description: instructor.description,
      tags,
      type: +instructor.type,
      internalId: +instructor.internalId,
      image: instructor.imageUrl,
    });

    if (instructor.availability && instructor.availability.length) {
      this.weekdaysFormArray.clear();
      instructor.availability.forEach((weekday) => {
        this.addWeekdaysRow({
          day: +weekday.weekday,
          time_from: weekday.timeFrom,
          time_to: weekday.timeTo,
        });
      });
    }

    if (instructor.qualification && instructor.qualification.length) {
      this.qualificationsFormArray.clear();
      instructor.qualification.forEach((qualification) => {
        this.qualificationsFormArray.push(this._formBuilder.control(qualification.qualification, [Validators.required]));
      });
    }

    this._syncAvailabilityLockState();
  }

  protected async _fetchInstructorById(id: number) {
    try {
      const res = await this.call(this._useInstructor.getInstructorById(id));
      if (res) {
        this.currentInstructor = res;
      }
      return res;
    } catch (err) {
      log.error('Error while fetching Instructor by Id', err);
      throw err;
    }
  }

  private _fetchInternalInstructors() {
    this._useInstructor
      .getAllInternalInstructorUsers()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.internalInstructorsUsers = res;
          }
        },
      });
  }

  private _initForm() {
    this.form = this._formBuilder.group({
      firstName: [null, [Validators.required]],
      lastName: [null, [Validators.required]],
      email: [null, [Validators.required, Validators.email]],
      phoneNo: [null],
      address: [null],
      description: [null, [Validators.required]],
      weekdays: this._formBuilder.array([]),
      qualifications: this._formBuilder.array([]),
      tags: [[]],
      type: [null, [Validators.required]],
      internalId: [null],
      image: [null],
    });

    this.form
      .get('type')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (type: InstructorType) => {
          if (type === InstructorType.Internal) {
            this.form.get('internalId').setValidators([Validators.required]);
          } else {
            this.form.get('internalId').setValidators(null);
          }

          this.form.get('internalId').updateValueAndValidity();
          this.form.updateValueAndValidity();
        },
      });

    this.form
      .get('internalId')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (internalId) => {
          if (internalId) {
            const selectedInternalInstructor = this.internalInstructorsUsers.find((user) => user.id === internalId);
            if (selectedInternalInstructor) {
              this.form.patchValue({
                firstName: selectedInternalInstructor.firstName,
                lastName: selectedInternalInstructor.lastName,
                email: selectedInternalInstructor.email,
                phoneNo: selectedInternalInstructor.phone,
                address: selectedInternalInstructor.addressLine,
              });
            }
          }
        },
      });
  }

  private _fetchTags() {
    this._useTags
      .getAllTagsByType(TagType.INSTRUCTOR)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.instructorTags = res.map((tag) => ({ label: tag.tagName, value: tag.tagName }));
          }
        },
      });
  }

  private _syncAvailabilityLockState() {
    if (this.hasConnectedBookings) {
      this.weekdaysFormArray.disable({ emitEvent: false });
      return;
    }

    this.weekdaysFormArray.enable({ emitEvent: false });
  }

  // private _uniqueDayValidatorFactory(weekdays: FormArray): ValidatorFn {
  //   return (control: AbstractControl): ValidationErrors | null => {
  //     const dayNumber = control.value;
  //     const dayOccurrences = weekdays.controls.filter((ctrl) => ctrl.get('day')?.value === dayNumber).length;
  //
  //     return dayOccurrences > 1 ? { uniqueDay: true } : null;
  //   };
  // }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }
}
